import {
  AzureSubscriptionRecord,
  AzureSubscriptionsApi,
  QuickLaunchLink,
  Resource,
  ResourceGroup,
  Subnet,
  TenantIdEmailPair,
} from 'cmp-client-api';

import type { MapperInterface } from '@src/server/converters/types/mapper.interface';
import type { AzureSubscriptionServiceInterface } from '@src/server';
import type { AzureSubscriptionInterface } from '@src/domain/types';
import { BrownfieldResourceInterface } from '@src/domain/pages/automation/provisioning/components/types/brownfield-resource.interface';

export class AzureSubscriptionService implements AzureSubscriptionServiceInterface {
  private readonly service: AzureSubscriptionsApi;

  private readonly serviceMapper: MapperInterface<
    AzureSubscriptionInterface[],
    AzureSubscriptionRecord[]
  >;

  private readonly quickLinksMapper: MapperInterface<QuickLaunchLink[], QuickLaunchLink[]>;

  private readonly brownfieldProvisioningMapper: MapperInterface<
    BrownfieldResourceInterface[],
    ResourceGroup[]
  >;

  private readonly brownfieldProvisioningVirtualNetworkMapper: MapperInterface<
    BrownfieldResourceInterface[],
    Resource[]
  >;

  private readonly brownfieldProvisioningSubnetMapper: MapperInterface<
    BrownfieldResourceInterface[],
    Subnet[]
  >;

  public constructor(
    service: AzureSubscriptionsApi,
    serviceMapper: MapperInterface<AzureSubscriptionInterface[], AzureSubscriptionRecord[]>,
    quickLinksMapper: MapperInterface<QuickLaunchLink[], QuickLaunchLink[]>,
    brownfieldProvisioningMapper: MapperInterface<BrownfieldResourceInterface[], ResourceGroup[]>,
    brownfieldProvisioningVirtualNetworkMapper: MapperInterface<
      BrownfieldResourceInterface[],
      Resource[]
    >,
    brownfieldProvisioningSubnetMapper: MapperInterface<BrownfieldResourceInterface[], Subnet[]>
  ) {
    this.service = service;
    this.serviceMapper = serviceMapper;
    this.quickLinksMapper = quickLinksMapper;
    this.brownfieldProvisioningMapper = brownfieldProvisioningMapper;
    this.brownfieldProvisioningVirtualNetworkMapper = brownfieldProvisioningVirtualNetworkMapper;
    this.brownfieldProvisioningSubnetMapper = brownfieldProvisioningSubnetMapper;
  }

  public refreshAzureSubscriptions(): Promise<TenantIdEmailPair> {
    return this.service.refreshAzureSubscriptions().then(({ data }) => data);
  }

  public getBrownFieldResources(subscriptionId: string): Promise<BrownfieldResourceInterface[]> {
    return this.service
      .getAzureResourceGroups(subscriptionId)
      .then(({ data }) => data.data)
      .then(this.brownfieldProvisioningMapper.transformFromApiModel);
  }

  public getVirtualNetworks(subscriptionId: string): Promise<BrownfieldResourceInterface[]> {
    return this.service
      .getAzureResources(subscriptionId)
      .then(({ data }) => data.data)
      .then(this.brownfieldProvisioningVirtualNetworkMapper.transformFromApiModel);
  }

  public getSubnet(
    subscriptionId: string,
    resourceGroupName: string,
    virtualNetworkName: string
  ): Promise<BrownfieldResourceInterface[]> {
    const areRequiredFieldsPopulated = subscriptionId && resourceGroupName && virtualNetworkName;

    if (!areRequiredFieldsPopulated) {
      return Promise.resolve([]);
    }

    return this.service
      .getAzureSubnets(subscriptionId, resourceGroupName, virtualNetworkName)
      .then(({ data }) => data.data)
      .then(this.brownfieldProvisioningSubnetMapper.transformFromApiModel);
  }
}
